import { apiSlice } from "../store/app.api";
import { supabase } from "../utils/API";

function fileExt(name: string) {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1) : "";
}

type SortBy = "CONTROL" | "LAST_NAME";
type SortDir = "asc" | "desc";

const authApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getDeputyUsers: build.query<
      { list: any[]; count: number },
      {
        page: number;
        perPage: number;
        selectedYear?: number;
        searchTerm?: string;
        sortBy?: "CONTROL" | "LAST_NAME";
        sortDir?: "asc" | "desc";
      }
    >({
      queryFn: async ({
        page,
        perPage,
        selectedYear,
        searchTerm,
        sortBy = "CONTROL",
        sortDir = "asc",
      }) => {
        const { data, error } = await supabase.rpc("get_deputy_users", {
          p_year: selectedYear ?? null,
          p_search: searchTerm?.trim() || null,
          p_sort_by: sortBy,
          p_sort_dir: sortDir,
          p_page: page,
          p_per_page: perPage,
        });

        if (error) throw error;

        return {
          data: {
            list: data?.data ?? [],
            count: data?.total ?? 0,
          },
        };
      },
      providesTags: ["users"],
    }),

    getUsers: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("users")
          .select("*")
          .eq("role", "USER")
          .in("status", ["VERIFIED", "BLOCKED"])
          .order("created_at", { ascending: false });
        return { data };
      },
      providesTags: ["users"],
    }),
    getAllUsers: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("users")
          .select("*")
          .in("role", ["USER", "DEPUTY"]);
        return { data };
      },
      providesTags: ["users"],
    }),
    // getUnVerifiedDeputyUsers: build.query<any, any>({
    //   queryFn: async () => {
    //     let { data } = await supabase
    //       .from("users")
    //       .select("*")
    //       .eq("status", "PENDING")
    //       .order("created_at", { ascending: false });
    //     return { data };
    //   },
    //   providesTags: ["users"],
    // }),
    getUnVerifiedDeputyUsers: build.query<
      any,
      { status?: "PENDING" | "REJECTED" }
    >({
      queryFn: async ({ status } = {}) => {
        let query = supabase.from("users").select("*");

        if (status) {
          query = query.eq("status", status);
        } else {
          query = query.in("status", ["PENDING", "REJECTED"]);
        }

        // ✅ Order by created_at descending
        query = query.order("created_at", { ascending: false });

        const { data, error } = await query;
        if (error) throw error;

        return { data };
      },
      providesTags: ["users"],
    }),

    addChannel: build.mutation<any, any>({
      queryFn: async (channel: any) => {
        let { data } = await supabase.from("channels").insert(channel).single();
        return { data };
      },
      invalidatesTags: ["channels"],
    }),
    addUser: build.mutation<any, any>({
      // payload shape we expect:
      // {
      //   ...formValues,
      //   profile_pic_file?: File | null
      // }
      queryFn: async (payload: any) => {
        try {
          const { profile_pic_file, ...user } = payload;

          // 1) Optional: upload profile image to Storage
          let profile_picture = user.profile_picture || null;

          if (profile_pic_file instanceof File) {
            const ext = fileExt(profile_pic_file.name) || "jpg";
            const path = `avatar/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images") // 👈 your bucket name
              .upload(path, profile_pic_file, {
                upsert: true,
                contentType: profile_pic_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            profile_picture = pub?.publicUrl ?? null;
          }

          // 2) Insert the user row (now with the image url + issued date)
          const rowToInsert = {
            ...user,
            profile_picture,
            // ensure your table expects ISO date (yyyy-mm-dd) or full timestamp
            card_issued_date: user.card_issued_date || null,
          };

          const { data, error } = await supabase
            .from("users")
            .insert(rowToInsert)
            .single();

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return { error: { status: 400, data: err.message ?? String(err) } };
        }
      },
      invalidatesTags: ["users"],
    }),
    addMTRCBUser: build.mutation<any, any>({
      queryFn: async (payload: any) => {
        try {
          const { profile_pic_file, id_image_file, ...user } = payload;

          let profile_picture = user.profile_picture || null;
          let id_url = user.id_url || null;

          if (profile_pic_file instanceof File) {
            const ext =
              profile_pic_file.name.split(".").pop()?.toLowerCase() || "jpg";
            const path = `avatar/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, profile_pic_file, {
                upsert: true,
                contentType: profile_pic_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            profile_picture = pub?.publicUrl ?? null;
          }

          if (id_image_file instanceof File) {
            const ext =
              id_image_file.name.split(".").pop()?.toLowerCase() || "jpg";
            const path = `id-image/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, id_image_file, {
                upsert: true,
                contentType: id_image_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            id_url = pub?.publicUrl ?? null;
          }

          const rowToInsert = {
            ...user,
            profile_picture,
            id_url,
            card_issued_date: user.card_issued_date || null,
          };

          const { data, error } = await supabase
            .from("users")
            .insert(rowToInsert)
            .select()
            .single();

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 400,
              data: err.message ?? String(err),
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),

    updateMTRCBUser: build.mutation<any, any>({
      queryFn: async (payload: any) => {
        try {
          const { id, profile_pic_file, id_image_file, ...user } = payload;

          let profile_picture = user.profile_picture || null;
          let id_url = user.id_url || null;

          if (profile_pic_file instanceof File) {
            const ext =
              profile_pic_file.name.split(".").pop()?.toLowerCase() || "jpg";
            const path = `avatar/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, profile_pic_file, {
                upsert: true,
                contentType: profile_pic_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            profile_picture = pub?.publicUrl ?? null;
          }

          if (id_image_file instanceof File) {
            const ext =
              id_image_file.name.split(".").pop()?.toLowerCase() || "jpg";
            const path = `id-image/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, id_image_file, {
                upsert: true,
                contentType: id_image_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            id_url = pub?.publicUrl ?? null;
          }

          const rowToUpdate = {
            ...user,
            profile_picture,
            id_url,
            card_issued_date: user.card_issued_date || null,
          };

          const { data, error } = await supabase
            .from("users")
            .update(rowToUpdate)
            .eq("id", id)
            .select()
            .single();

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 400,
              data: err.message ?? String(err),
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),

    addAdminUser: build.mutation<any, any>({
      queryFn: async (payload: any) => {
        try {
          const { profile_pic_file, ...user } = payload;

          let profile_picture = user.profile_picture || null;

          if (profile_pic_file instanceof File) {
            const ext =
              profile_pic_file.name.split(".").pop()?.toLowerCase() || "jpg";
            const path = `avatar/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, profile_pic_file, {
                upsert: true,
                contentType: profile_pic_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            profile_picture = pub?.publicUrl ?? null;
          }

          const rowToInsert = {
            ...user,
            profile_picture,
          };

          const { data, error } = await supabase
            .from("users")
            .insert(rowToInsert)
            .select()
            .single();

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 400,
              data: err.message ?? String(err),
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),

    updateAdminUser: build.mutation<any, any>({
      queryFn: async (payload: any) => {
        try {
          const { id, profile_pic_file, ...user } = payload;

          let profile_picture = user.profile_picture || null;

          if (profile_pic_file instanceof File) {
            const ext =
              profile_pic_file.name.split(".").pop()?.toLowerCase() || "jpg";
            const path = `avatar/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, profile_pic_file, {
                upsert: true,
                contentType: profile_pic_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            profile_picture = pub?.publicUrl ?? null;
          }

          const rowToUpdate = {
            ...user,
            profile_picture,
          };

          const { data, error } = await supabase
            .from("users")
            .update(rowToUpdate)
            .eq("id", id)
            .select()
            .single();

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 400,
              data: err.message ?? String(err),
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    updateDeputyUser: build.mutation<any, any>({
      queryFn: async (payload: any) => {
        try {
          const { id, profile_pic_file, ...user } = payload;
          if (!id) {
            return { error: { message: "User ID is required for update" } };
          }

          let profile_picture = user.profile_picture ?? null;

          if (profile_pic_file instanceof File) {
            const ext = fileExt(profile_pic_file.name) || "jpg";
            const path = `avatar/${crypto.randomUUID()}.${ext}`;

            const { error: uploadErr } = await supabase.storage
              .from("images")
              .upload(path, profile_pic_file, {
                upsert: true,
                contentType: profile_pic_file.type || "image/jpeg",
              });

            if (uploadErr) throw uploadErr;

            const { data: pub } = supabase.storage
              .from("images")
              .getPublicUrl(path);

            profile_picture = pub?.publicUrl ?? null;
          }

          const rowToUpdate = {
            ...user,
            profile_picture,
            card_issued_date: user.card_issued_date || null,
          };

          const { data, error } = await supabase
            .from("users")
            .update(rowToUpdate)
            .eq("id", id)
            .select()
            .single();

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 400,
              data: err.message ?? String(err),
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    updateUser: build.mutation<any, any>({
      queryFn: async (user: any) => {
        // Ensure the user object has an id field to identify the row to update
        const { id, ...rest } = user;
        if (!id) {
          return { error: { message: "User ID is required for update" } };
        }

        let { data, error } = await supabase
          .from("users")
          .update(rest)
          .eq("id", id)
          .select();

        if (error) {
          return { error };
        }

        return { data };
      },
      invalidatesTags: ["users"],
    }),

    updateUserRole: build.mutation<any, any>({
      queryFn: async (params: any) => {
        let { data, error } = await supabase
          .from("users")
          .update({ sub_role: params.role, role: "DEPUTY" })
          .eq("id", params.id)
          .select();
        if (error) {
          return { error };
        }

        return { data };
      },
      invalidatesTags: ["users"],
    }),

    addProgram: build.mutation<any, any>({
      queryFn: async (program: any) => {
        let { data } = await supabase
          .from("tv_schedule_program")
          .insert(program)
          .single();
        return { data };
      },
      invalidatesTags: ["channels"],
    }),
    deleteProgram: build.mutation<any, any>({
      queryFn: async (id: any) => {
        let { data } = await supabase
          .from("tv_schedule_program")
          .delete()
          .eq("id", id);
        return { data };
      },
      invalidatesTags: ["channels"],
    }),
    deleteUser: build.mutation<any, any>({
      queryFn: async (id: any) => {
        let { data } = await supabase.from("users").delete().eq("id", id);

        return { data };
      },
      invalidatesTags: ["users"],
    }),
    declineUser: build.mutation<any, any>({
      queryFn: async ({ id, reasonStr }) => {
        let { data: userData, error: error1 } = await supabase
          .from("users")
          .update({ status: "REJECTED" })
          .eq("id", id)
          .select();
        if (error1) return { error: error1 };

        let { error: error2 } = await supabase
          .from("user_rejections")
          .insert([{ user_id: id, reason: reasonStr }]);
        if (error2) return { error: error2 };

        await fetch(
          "https://mtrcb-app-server.vercel.app/api/email/send/reject-user",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              recipient: userData![0].email,
              first_name: userData![0].name,
              last_name: userData![0].last_name,
              reason: reasonStr,
            }),
          },
        );

        return { data: userData };
      },
      invalidatesTags: ["users"],
    }),
    approveUser: build.mutation<any, any>({
      queryFn: async (id: any) => {
        let { data } = await supabase
          .from("users")
          .update({ status: "VERIFIED" })
          .eq("id", id)
          .select();
        return { data };
      },
      invalidatesTags: ["users"],
    }),
    getDeputyRegisterRequests: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("deputy_register_requests")
          .select("*");
        return { data };
      },
      providesTags: ["users"],
    }),
    approveDeputyUser: build.mutation<any, any>({
      queryFn: async (id: any) => {
        let { data } = await supabase
          .from("deputy_register_requests")
          .update({ status: "VERIFIED" })
          .eq("id", id)
          .select();
        return { data };
      },
      invalidatesTags: ["users"],
    }),

    getMTRCBUsers: build.query<
      { list: any[]; count: number },
      {
        page?: number;
        rowsPerPage?: number;
        searchName?: string;
        searchEmployeeId?: string;
        searchTerm?: string;
        sortBy?: "EMPLOYEE_ID" | "LAST_NAME" | "CREATED_AT";
        sortDir?: "asc" | "desc";
        subRole?: "ALL" | "MTRCB" | "AMITY" | "LEGACY";
      }
    >({
      queryFn: async ({
        page = 1,
        rowsPerPage = 10,
        searchName = "",
        searchEmployeeId = "",
        searchTerm = "",
        sortBy = "CREATED_AT",
        sortDir = "desc",
        subRole = "ALL",
      }) => {
        const from = (page - 1) * rowsPerPage;
        const to = from + rowsPerPage - 1;

        let query = supabase
          .from("users")
          .select("*", { count: "exact" })
          .eq("role", "DEPUTY");

        if (subRole && subRole !== "ALL") {
          query = query.eq("sub_role", subRole);
        } else {
          query = query.in("sub_role", ["MTRCB", "AMITY", "LEGACY"]);
        }

        const keyword = searchTerm.trim();

        if (keyword) {
          query = query.or(
            `name.ilike.%${keyword}%,last_name.ilike.%${keyword}%,emp_id.ilike.%${keyword}%,emp_position.ilike.%${keyword}%`,
          );
        } else {
          if (searchName?.trim()) {
            const nameKeyword = searchName.trim();

            query = query.or(
              `name.ilike.%${nameKeyword}%,last_name.ilike.%${nameKeyword}%`,
            );
          }

          if (searchEmployeeId?.trim()) {
            query = query.ilike("emp_id", `%${searchEmployeeId.trim()}%`);
          }
        }

        const sortColumn =
          sortBy === "EMPLOYEE_ID"
            ? "emp_id"
            : sortBy === "LAST_NAME"
              ? "last_name"
              : "created_at";

        const { data, error, count } = await query
          .order(sortColumn, { ascending: sortDir === "asc" })
          .range(from, to);

        if (error) {
          return { error };
        }

        return {
          data: {
            list: data ?? [],
            count: count ?? 0,
          },
        };
      },
      providesTags: ["users"],
    }),

    getAdminUsers: build.query<
      { list: any[]; count: number },
      {
        page?: number;
        rowsPerPage?: number;
        searchTerm?: string;
        sortBy?: "NAME" | "LAST_NAME" | "CREATED_AT";
        sortDir?: "asc" | "desc";
      }
    >({
      queryFn: async ({
        page = 1,
        rowsPerPage = 10,
        searchTerm = "",
        sortBy = "CREATED_AT",
        sortDir = "desc",
      }) => {
        const from = (page - 1) * rowsPerPage;
        const to = from + rowsPerPage - 1;

        let query = supabase
          .from("users")
          .select("*", { count: "exact" })
          .eq("role", "ADMIN");

        const keyword = searchTerm.trim();
        if (keyword) {
          query = query.or(
            `name.ilike.%${keyword}%,last_name.ilike.%${keyword}%,email.ilike.%${keyword}%`
          );
        }

        const sortColumn =
          sortBy === "NAME"
            ? "name"
            : sortBy === "LAST_NAME"
            ? "last_name"
            : "created_at";

        const { data, error, count } = await query
          .order(sortColumn, { ascending: sortDir === "asc" })
          .range(from, to);

        if (error) {
          return { error };
        }

        return {
          data: {
            list: data ?? [],
            count: count ?? 0,
          },
        };
      },
      providesTags: ["users"],
    }),


    changeDeputyPassword: build.mutation<any, { user_id: string; password: string }>({
      queryFn: async ({ user_id, password }) => {
        try {
          if (!user_id) {
            return {
              error: { status: 400, data: "Supabase Auth user_id is required." },
            };
          }

          if (!password || password.length < 8) {
            return {
              error: { status: 400, data: "Password must be at least 8 characters long." },
            };
          }

          const { data, error } = await supabase.auth.admin.updateUserById(
            user_id,
            { password },
          );

          if (error) throw error;

          return { data };
        } catch (err: any) {
          return {
            error: {
              status: 400,
              data: err?.message ?? String(err),
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),

    changeUserEmail: build.mutation<
      any,
      { user_id: string; new_email: string; users_id: string; is_deputy?: boolean; deputy_control_number?: string }
    >({
      queryFn: async ({ user_id, new_email, users_id, is_deputy = false, deputy_control_number }) => {
        try {
          if (!user_id) {
            return { error: { status: 400, data: "Supabase Auth user_id is required." } };
          }

          const emailTrimmed = new_email.trim().toLowerCase();
          if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
            return { error: { status: 400, data: "A valid email address is required." } };
          }

          // 1) Pre-check email availability in auth.users and public.users
          const { data: isAvailable, error: checkError } = await supabase.rpc(
            "check_email_available",
            { p_email: emailTrimmed, p_user_id: user_id }
          );

          if (!checkError && isAvailable === false) {
            return {
              error: {
                status: 400,
                data: `The email "${emailTrimmed}" is already registered to another account in the system.`,
              },
            };
          }

          // 2) Update Supabase Auth email (admin, immediately confirmed)
          const { error: authError } = await supabase.auth.admin.updateUserById(
            user_id,
            { email: emailTrimmed, email_confirm: true }
          );

          if (authError) {
            const errMsg = authError.message || "";
            if (
              errMsg.toLowerCase().includes("duplicate") ||
              errMsg.toLowerCase().includes("unique") ||
              errMsg.toLowerCase().includes("already") ||
              errMsg.toLowerCase().includes("error updating user")
            ) {
              return {
                error: {
                  status: 400,
                  data: `The email "${emailTrimmed}" is already registered to another account in the system.`,
                },
              };
            }
            throw authError;
          }

          // 3) Sync public.users table
          const { error: usersError } = await supabase
            .from("users")
            .update({ email: emailTrimmed })
            .eq("id", users_id);
          if (usersError) throw usersError;

          // 4) If deputy, update the latest deputy_records row by control_number
          if (is_deputy && deputy_control_number) {
            // Fetch the latest deputy_records row for this control number
            const { data: records, error: fetchError } = await supabase
              .from("deputy_records")
              .select("id")
              .eq("control_number", deputy_control_number)
              .order("id", { ascending: false })
              .limit(1);

            if (fetchError) throw fetchError;

            if (records && records.length > 0) {
              const { error: recordError } = await supabase
                .from("deputy_records")
                .update({ email: emailTrimmed })
                .eq("id", records[0].id);
              if (recordError) throw recordError;
            }
          }

          return { data: { success: true, email: emailTrimmed } };
        } catch (err: any) {
          return { error: { status: 400, data: err?.message ?? String(err) } };
        }
      },
      invalidatesTags: ["users"],
    }),

    blockUser: build.mutation<any, any>({
      queryFn: async (params: any) => {
        // First query to update user metadata
        let { data: userData, error: userError } =
          await supabase.auth.admin.updateUserById(params.user_id, {
            user_metadata: { session_invalidated: true },
          });

        if (userError) {
          return { error: userError };
        }

        // Second query - replace this with your specific query
        let { data: secondData, error: secondError } = await supabase
          .from("users") // Specify your table
          .update({ status: params.status }) // Example update
          .eq("id", params.id); // Use your specific condition

        if (secondError) {
          return { error: secondError };
        }

        // Combine both responses or return as needed
        return { data: { userData, secondData } };
      },
      invalidatesTags: ["users"],
    }),

    blockAllUser: build.mutation<any, any>({
      queryFn: async () => {
        let { data: secondData, error: secondError } = await supabase
          .from("users")
          .update({ status: "BLOCKED" })
          .eq("role", "DEPUTY")
          .eq("sub_role", "DEPUTY");

        if (secondError) {
          return { error: secondError };
        }

        return { data: { secondData } };
      },
      invalidatesTags: ["users"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDeputyUsersQuery,
  useAddChannelMutation,
  useDeleteUserMutation,
  useBlockUserMutation,
  useBlockAllUserMutation,
  useGetUsersQuery,
  useAddProgramMutation,
  useDeleteProgramMutation,
  useApproveUserMutation,
  useDeclineUserMutation,
  useGetAllUsersQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useUpdateDeputyUserMutation,
  useAddMTRCBUserMutation,
  useUpdateMTRCBUserMutation,
  useGetUnVerifiedDeputyUsersQuery,
  useGetDeputyRegisterRequestsQuery,
  useApproveDeputyUserMutation,
  useUpdateUserRoleMutation,
  useGetMTRCBUsersQuery,
  useGetAdminUsersQuery,
  useAddAdminUserMutation,
  useUpdateAdminUserMutation,
  useChangeDeputyPasswordMutation,
  useChangeUserEmailMutation,
  useLazyGetDeputyUsersQuery,
  useLazyGetUsersQuery,
  useLazyGetUnVerifiedDeputyUsersQuery,
  useLazyGetMTRCBUsersQuery,
  useLazyGetAdminUsersQuery,
} = authApi;
